import { FormControl, Grid, MenuItem, Select, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { CollapsibleTable, CollapsibleTableCell } from "../../components/CollapsibleTable";
import ScrollIndicatorsComp from '../../components/ScrollIndicatorsComp/ScrollIndicatorsComp';
import { PlaceholderStyle, SelectMenuStyle } from '../casual-board/styled';
import { IDailyAllocationsData } from './VesselForecastData';
import { AllocationType, ISLANDPORTS, LOCATIONS, Locations, MAINLANDPORTS } from './VesselForecastModels';
import { hideScrollBar, selectStyle, shiftDetailContainerStyle } from "./styled";

export const DailyAllocations = ({ data, handleLocationSelect, handlePortSelect }: {
  data: IDailyAllocationsData,
  handleLocationSelect: (allocationType: AllocationType, location: string) => void
  handlePortSelect: (allocationType: AllocationType, location: string, port: string) => void
}) => {
  const getShiftHeaders = (shiftHeaders: string[]) => {
    return shiftHeaders.map(sh => {
      const [date, time] = sh.split(",");
      return (
        <Box>
          <Typography>{date}</Typography>
          <Typography>{time}</Typography>
        </Box>
      )
    });
  }

  const cols: CollapsibleTableCell[] = [
    'Vessel',
    'Loc.',
    'Stev.',
    'Agent',
    ...getShiftHeaders(data.shiftHeaders),
    'Cargo'
  ].map(title => ({ title, align: "left" }));

  const isMainland = Locations[data.selectedLocation as keyof typeof Locations] === Locations.mainland;

  const shiftDetail = (shiftCount: string, shift: string | null) => {
    return (
      <Box sx={shiftDetailContainerStyle}>
        <Typography sx={{ width: 30 }}>{shiftCount}</Typography>
        <Typography>{shift ?? ""}</Typography>
      </Box>
    );
  }

  const vesselForecast = data.vesselForecast?.map(v => {
    return {
      vessel: v.vessel,
      location: v.location,
      stevedoring: v.stevedoring,
      agent: v.agent,
      shift1: shiftDetail(v.shift1Count, v.shift1),
      shift2: shiftDetail(v.shift2Count, v.shift2),
      shift3: shiftDetail(v.shift3Count, v.shift3),
      cargo: v.cargo
    }
  }) ?? [];

  const ports: {
    code: string;
    display: string
  }[] = isMainland ? MAINLANDPORTS : ISLANDPORTS;

  return (
    <>
      <Grid container>
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <FormControl fullWidth>
            <Select
              sx={selectStyle}
              displayEmpty
              value={data.selectedLocation}
              data-automation="location-dropdown"
              onChange={e => handleLocationSelect(
                AllocationType.daily,
                e.target.value,
              )}
              MenuProps={{ sx: SelectMenuStyle }}
              renderValue={(location) => {
                if (!location) return (
                  <Typography sx={PlaceholderStyle}>
                    Select a location
                  </Typography>
                )
                return LOCATIONS.filter(b => b.code === location).map(b => b.display)
              }}
            >
              {LOCATIONS.map(({ code, display }) => (
                <MenuItem key={code} value={code}>
                  {display}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <Select
              sx={selectStyle}
              displayEmpty
              value={data.selectedPort}
              data-automation="port-dropdown"
              onChange={e => handlePortSelect(
                AllocationType.daily,
                data.selectedLocation,
                e.target.value
              )}
              MenuProps={{ sx: SelectMenuStyle }}
              renderValue={(port) => {
                if (!port) return (
                  <Typography sx={PlaceholderStyle}>
                    Select a port
                  </Typography>
                )
                return ports.filter(p => p.code === port).map(p => p.display);
              }}
            >
              {ports.map(({ code, display }) => (
                <MenuItem key={code} value={code}>
                  {display}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Box sx={hideScrollBar}>
        <Typography 
          component={'div'} 
          variant='container' 
          data-automation="data-container"
        >
          <ScrollIndicatorsComp>
            <CollapsibleTable
              noDataMsg="NO WORKING VESSELS IN PORT"
              cols={cols}
              rows={vesselForecast}
              isOneLevel
              footerRowIndexes={data.footerRowIndexes}
            />
          </ScrollIndicatorsComp>
        </Typography>
      </Box>
      <Typography>{data.statusLegend}</Typography>
      <Typography style={{ display: 'inline', paddingRight: "3px", fontWeight: "bold" }}>
        {data.legend && "Legend:"}
      </Typography>
      <Typography style={{ display: 'inline' }}>
        {data.legend}
      </Typography>
    </>
  )
}